import { Directive, ElementRef, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';

/** How long a pause resets the typed buffer — same feel as a native <select>. */
const RESET_MS = 1000;

/**
 * Native-<select> letter jumping for a mat-select.
 *
 * mat-select replaced native selects so their option lists could be
 * height-capped, but its own typeahead doesn't behave like a native select:
 * pressing the same letter again doesn't step to the next match. This restores
 * the native behaviour:
 *
 *  - the same letter repeated (u, u, u …) cycles through the options starting
 *    with it, wrapping at the end;
 *  - letters typed quickly form a prefix ("uni" → United …), reset after a
 *    1 s pause;
 *  - list closed: the match is selected straight away (selectionChange fires,
 *    so the filter applies), exactly like a native select;
 *  - list open: the match is highlighted and scrolled into view; Enter/click
 *    picks it.
 *
 * Moving mat-select's own key manager is what produces both closed/open
 * outcomes — MatSelect subscribes to its change stream. Letters are caught in
 * the capture phase and stopped, so MatSelect's built-in typeahead never sees
 * them and the two can't disagree.
 *
 * Usage: <mat-select gjSelectTypeahead …>
 */
@Directive({
  selector: 'mat-select[gjSelectTypeahead]',
  standalone: true,
})
export class SelectTypeaheadDirective implements OnInit, OnDestroy {
  private readonly select = inject(MatSelect);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);

  private buffer = '';
  private lastKeyAt = 0;

  private readonly onKeydown = (event: KeyboardEvent) => {
    const key = event.key;
    // Printable single characters only; leave arrows, Enter, Tab, shortcuts
    // and a leading Space (which opens the list) to MatSelect.
    if (!key || key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    const now = Date.now();
    if (now - this.lastKeyAt > RESET_MS) {
      this.buffer = '';
    }
    if (key === ' ' && !this.buffer) {
      return;
    }
    this.lastKeyAt = now;
    this.buffer += key.toLowerCase();

    event.preventDefault();
    event.stopImmediatePropagation();

    const index = this.findMatch();
    if (index < 0) {
      return;
    }
    this.zone.run(() => {
      const manager = this.select._keyManager;
      if (!this.select.panelOpen && manager.activeItemIndex === index) {
        // Closed, and the key manager is already parked on the match (left
        // over from an earlier open) — setActiveItem wouldn't emit a change,
        // so MatSelect wouldn't select it. Select it directly instead.
        this.select.options.get(index)?._selectViaInteraction();
      } else {
        manager.setActiveItem(index);
      }
    });
  };

  ngOnInit(): void {
    // Capture phase: runs before MatSelect's own keydown listener on the same
    // element, so stopImmediatePropagation keeps its typeahead out of it.
    this.zone.runOutsideAngular(() =>
      this.host.nativeElement.addEventListener('keydown', this.onKeydown, true)
    );
  }

  ngOnDestroy(): void {
    this.host.nativeElement.removeEventListener('keydown', this.onKeydown, true);
  }

  private findMatch(): number {
    const options: MatOption[] = this.select.options?.toArray() ?? [];
    if (!options.length) {
      return -1;
    }
    const labels = options.map(o => (o.disabled ? null : o.getLabel().trim().toLowerCase()));

    // Where we are now: the highlighted option while open, else the selection.
    const current = this.select.panelOpen
      ? this.select._keyManager.activeItemIndex ?? -1
      : options.findIndex(o => o.selected);

    // "u", "uu", "uuu" … → step to the next option starting with "u".
    const cycling = [...this.buffer].every(ch => ch === this.buffer[0]);
    const needle = cycling ? this.buffer[0] : this.buffer;
    // Cycling moves past the current option; a longer prefix may stay on it
    // ("u" landed on United Kingdom, then "un" still matches it).
    const start = cycling ? current + 1 : Math.max(current, 0);

    for (let i = 0; i < options.length; i++) {
      const idx = (start + i) % options.length;
      const label = labels[idx];
      if (label !== null && label.startsWith(needle)) {
        return idx;
      }
    }
    return -1;
  }
}
